import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';

interface SiteSetting {
  id: number;
  key: string;
  value: string;
  label: string | null;
  description: string | null;
}

const EDGE_FUNCTION_URL = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/sports-rss-fetcher`;

export default function AdminSettings() {
  const [settings, setSettings] = useState<SiteSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<Record<number, boolean>>({});
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [fetchingRss, setFetchingRss] = useState(false);
  const [rssResult, setRssResult] = useState<string | null>(null);

  // Image upload state
  const [uploadingImage, setUploadingImage] = useState<Record<number, boolean>>({});
  const fileInputRefs = useRef<Record<number, HTMLInputElement | null>>({});

  useEffect(() => {
    fetchSettings();
  }, []);

  async function fetchSettings() {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('site_settings')
        .select('*')
        .order('id');

      if (data && !error) {
        setSettings(data);
      }
    } catch {
      setMessage({ type: 'error', text: '설정을 불러오는데 실패했습니다.' });
    } finally {
      setLoading(false);
    }
  }

  async function handleSave(setting: SiteSetting) {
    setSaving((prev) => ({ ...prev, [setting.id]: true }));
    setMessage(null);

    try {
      const { error } = await supabase
        .from('site_settings')
        .update({ value: setting.value, updated_at: new Date().toISOString() })
        .eq('id', setting.id);

      if (error) {
        setMessage({ type: 'error', text: '저장에 실패했습니다: ' + error.message });
      } else {
        setMessage({ type: 'success', text: '저장되었습니다.' });
      }
    } catch {
      setMessage({ type: 'error', text: '저장 중 오류가 발생했습니다.' });
    } finally {
      setSaving((prev) => ({ ...prev, [setting.id]: false }));
    }
  }

  async function handleRssFetch() {
    setFetchingRss(true);
    setRssResult(null);
    setMessage(null);

    try {
      const response = await fetch(EDGE_FUNCTION_URL, { method: 'POST' });
      const data = await response.json();

      if (data.success) {
        setRssResult(
          `수집 완료! ${data.feeds_processed}개 피드 처리, ${data.new_articles}개 새 기사 추가됨. ${
            data.errors && data.errors.length > 0
              ? '일부 오류: ' + data.errors.join(', ')
              : ''
          }`
        );
      } else {
        setRssResult('수집 실패: ' + (data.error || '알 수 없는 오류'));
      }
    } catch (e) {
      setRssResult('수집 중 오류: ' + (e instanceof Error ? e.message : '알 수 없는 오류'));
    } finally {
      setFetchingRss(false);
    }
  }

  function handleChange(id: number, value: string) {
    setSettings((prev) =>
      prev.map((s) => (s.id === id ? { ...s, value } : s))
    );
  }

  function isImageSetting(key: string): boolean {
    return key.includes('_image');
  }

  async function handleImageUpload(settingId: number, e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: '파일 크기는 5MB 이하여야 합니다' });
      return;
    }

    if (!['image/png', 'image/jpeg', 'image/gif', 'image/webp'].includes(file.type)) {
      setMessage({ type: 'error', text: 'PNG, JPEG, GIF, WebP 형식만 지원합니다' });
      return;
    }

    setUploadingImage((prev) => ({ ...prev, [settingId]: true }));
    setMessage(null);

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `settings/${settingId}-${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('admin-assets')
        .upload(fileName, file, { upsert: true, contentType: file.type });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage.from('admin-assets').getPublicUrl(fileName);

      // Update local state
      setSettings((prev) =>
        prev.map((s) => (s.id === settingId ? { ...s, value: urlData.publicUrl } : s))
      );

      setMessage({ type: 'success', text: '이미지가 업로드되었습니다. 저장 버튼을 눌러 적용하세요.' });
    } catch {
      setMessage({ type: 'error', text: '이미지 업로드에 실패했습니다' });
    } finally {
      setUploadingImage((prev) => ({ ...prev, [settingId]: false }));
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-lg font-bold text-foreground-800 mb-5">사이트 설정</h2>

      {/* RSS Feed Fetch Section */}
      <div className="bg-background-100 border border-background-300 rounded-lg p-5 mb-6">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <h3 className="text-sm font-bold text-foreground-700 flex items-center gap-2">
              <span className="w-4 h-4 flex items-center justify-center text-accent-500">
                <i className="ri-rss-line" />
              </span>
              RSS 경기분석 수동 수집
            </h3>
            <p className="text-xs text-foreground-600 mt-1">
              24개 피드(축구/야구/농구/하키/배구)에서 새 기사를 즉시 가져옵니다. 평소에는 30분 주기로 자동 수집됩니다.
            </p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <button
            onClick={handleRssFetch}
            disabled={fetchingRss}
            className="px-5 py-2.5 text-sm font-bold bg-accent-500 text-foreground-50 rounded-lg hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {fetchingRss ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-foreground-50 border-t-transparent rounded-full animate-spin" />
                수집 중...
              </span>
            ) : (
              '지금 RSS 수집하기'
            )}
          </button>
          {rssResult && (
            <span className={`text-xs font-medium ${
              rssResult.includes('실패') || rssResult.includes('오류')
                ? 'text-red-400'
                : 'text-accent-500'
            }`}>
              {rssResult}
            </span>
          )}
        </div>
      </div>

      {message && (
        <div
          className={`mb-4 px-4 py-2.5 rounded-lg text-sm font-medium ${
            message.type === 'success'
              ? 'bg-accent-500/10 text-accent-600 border border-accent-500/20'
              : 'bg-red-500/10 text-red-500 border border-red-500/20'
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="space-y-4">
        {settings.map((setting) => (
          <div
            key={setting.id}
            className="bg-background-100 border border-background-300 rounded-lg p-5"
          >
            <div className="flex items-start justify-between gap-4 mb-3">
              <div>
                <h3 className="text-sm font-bold text-foreground-700">
                  {setting.label || setting.key}
                </h3>
                {setting.description && (
                  <p className="text-xs text-foreground-600 mt-1">{setting.description}</p>
                )}
                <p className="text-[10px] text-foreground-600 mt-1 font-mono">
                  Key: {setting.key}
                </p>
              </div>
            </div>
            {isImageSetting(setting.key) ? (
              <div className="space-y-3">
                {setting.value && (
                  <div className="w-full max-w-md rounded-lg overflow-hidden border border-background-300 bg-background-200">
                    <img
                      src={setting.value}
                      alt={setting.label || setting.key}
                      className="w-full h-48 object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                )}
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="text"
                    value={setting.value}
                    onChange={(e) => handleChange(setting.id, e.target.value)}
                    placeholder="이미지 URL을 직접 입력하거나 파일을 업로드하세요"
                    className="flex-1 px-3 py-2 text-sm border border-background-300 rounded-lg bg-background-50 text-foreground-700 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500"
                  />
                  <button
                    onClick={() => fileInputRefs.current[setting.id]?.click()}
                    disabled={uploadingImage[setting.id]}
                    className="px-4 py-2 text-sm font-medium bg-background-200 text-foreground-600 rounded-lg hover:bg-background-300 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 flex items-center gap-1.5"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-upload-cloud-2-line" />
                    </span>
                    {uploadingImage[setting.id] ? '업로드 중...' : '이미지 업로드'}
                  </button>
                  <input
                    ref={(el) => { fileInputRefs.current[setting.id] = el; }}
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/webp"
                    onChange={(e) => handleImageUpload(setting.id, e)}
                    className="hidden"
                  />
                  <button
                    onClick={() => handleSave(setting)}
                    disabled={saving[setting.id]}
                    className="px-4 py-2 text-sm font-bold bg-primary-500 text-foreground-50 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving[setting.id] ? '저장 중...' : '저장'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  value={setting.value}
                  onChange={(e) => handleChange(setting.id, e.target.value)}
                  className="flex-1 px-3 py-2 text-sm border border-background-300 rounded-lg bg-background-50 text-foreground-700 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500"
                />
                <button
                  onClick={() => handleSave(setting)}
                  disabled={saving[setting.id]}
                  className="px-4 py-2 text-sm font-bold bg-primary-500 text-foreground-50 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving[setting.id] ? '저장 중...' : '저장'}
                </button>
              </div>
            )}
          </div>
        ))}

        {settings.length === 0 && (
          <div className="text-center py-20 text-foreground-600">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-background-200 flex items-center justify-center">
              <i className="ri-settings-3-line text-xl" />
            </div>
            <p className="text-sm">등록된 설정이 없습니다.</p>
          </div>
        )}
      </div>
    </div>
  );
}